import { useLabelVersion } from '../../../../features/labels/LabelsProvider';
import { labelRegion, labelArea, labelCentro } from '../../../../features/labels/labels';
import React from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';

import { canViewAlarmReaction } from '../../../../features/ai-usage/aiUsageClient';
import {
  buildAnaliticaSheets,
  DEFAULT_ANALYTICS_CONTENT,
  estimateDailyDetailRows,
  getAnaliticaContentAvailability,
} from '../../pages/Dashboard/export/buildAnaliticaSheets';
import { buildXlsx, downloadXlsx, sanitizeFileName } from '../../utils/xlsx/miniXlsx';
import ScappSwitch from './ScappSwitch';

export const ANALYTICS_XLSX_PREFS_KEY = 'scappXlsxExportPrefs';

const CONTENT_OPTIONS = [
  ['summary', 'export_content_summary', 'Resumen y métricas'],
  ['periodDetail', 'export_content_period_detail', 'Detalle por periodo'],
  ['alarmRate', 'export_content_alarm_rate', 'Alarmas por tasa'],
  ['ranking', 'export_content_ranking', 'Ranking'],
  ['alarmEvents', 'export_content_alarm_events', 'Alarmas (eventos)'],
  ['notes', 'export_content_notes', 'Notas'],
  ['dailyDetail', 'export_content_daily_detail', 'Detalle diario por jaula'],
];

const defaultPreferences = () => ({
  content: { ...DEFAULT_ANALYTICS_CONTENT },
  dailyDetailRange: 'full',
  dailyDetailCages: 'all',
});

export const readAnalyticsXlsxPreferences = (storage, hasSelectedDay) => {
  const defaults = defaultPreferences();
  try {
    const stored = JSON.parse(storage?.getItem(ANALYTICS_XLSX_PREFS_KEY) || 'null');
    if (stored?.version !== 1) return defaults;
    const content = Object.fromEntries(Object.keys(DEFAULT_ANALYTICS_CONTENT).map((key) => [
      key,
      typeof stored.content?.[key] === 'boolean' ? stored.content[key] : defaults.content[key],
    ]));
    const storedRange = ['full', 'day'].includes(stored.dailyDetailRange)
      ? stored.dailyDetailRange
      : 'full';
    return {
      content,
      dailyDetailRange: storedRange === 'day' && !hasSelectedDay ? 'full' : storedRange,
      dailyDetailCages: 'all',
    };
  } catch (_error) {
    return defaults;
  }
};

export const writeAnalyticsXlsxPreferences = (storage, content, dailyDetailRange) => {
  try {
    storage?.setItem(ANALYTICS_XLSX_PREFS_KEY, JSON.stringify({
      version: 1,
      content: Object.fromEntries(Object.keys(DEFAULT_ANALYTICS_CONTENT).map((key) => [
        key,
        typeof content?.[key] === 'boolean' ? content[key] : DEFAULT_ANALYTICS_CONTENT[key],
      ])),
      dailyDetailRange: ['full', 'day'].includes(dailyDetailRange) ? dailyDetailRange : 'full',
    }));
  } catch (_error) {}
};

const translated = (translate, key, fallback) => {
  const value = typeof translate === 'function' ? translate(key) : key;
  return value && value !== key ? value : fallback;
};

const sectionTitleSx = {
  mb: 1,
  color: '#e84e0e',
  fontSize: '.62vw',
  fontWeight: 800,
  letterSpacing: '.04em',
  textTransform: 'uppercase',
};

const AnalyticsXlsxExportButton = ({
  data,
  dias,
  nivelSeleccionado,
  groupMode,
  compact,
  selectedDay,
  selectedRateKey = 'auto',
  selectedEntity,
  region,
  area,
  center,
  translate,
  registerXlsxExport,
  clearXlsxExport,
}) => {
  useLabelVersion();
  const defaults = React.useMemo(defaultPreferences, []);
  const [open, setOpen] = React.useState(false);
  const [content, setContent] = React.useState(defaults.content);
  const [dailyDetailRange, setDailyDetailRange] = React.useState(defaults.dailyDetailRange);
  const [dailyDetailCages, setDailyDetailCages] = React.useState(defaults.dailyDetailCages);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const t = React.useCallback((key, fallback) => translated(translate, key, fallback), [translate]);
  const availability = React.useMemo(
    () => getAnaliticaContentAvailability(data, nivelSeleccionado, compact),
    [compact, data, nivelSeleccionado],
  );
  const hasExportableContent = Object.values(availability).some(Boolean);
  const dailyDetailEnabled = content.dailyDetail && availability.dailyDetail;

  const openDialog = React.useCallback(() => {
    const preferences = readAnalyticsXlsxPreferences(window.localStorage, Boolean(selectedDay));
    setContent(preferences.content);
    setDailyDetailRange(preferences.dailyDetailRange);
    setDailyDetailCages('all');
    setError('');
    setOpen(true);
  }, [selectedDay]);

  React.useEffect(() => {
    if (!selectedDay && dailyDetailRange === 'day') setDailyDetailRange('full');
  }, [dailyDetailRange, selectedDay]);

  React.useEffect(() => {
    if (!selectedEntity && dailyDetailCages === 'selected') setDailyDetailCages('all');
  }, [dailyDetailCages, selectedEntity]);

  React.useEffect(() => {
    if (typeof registerXlsxExport !== 'function') return undefined;
    registerXlsxExport({
      id: 'analytics',
      run: openDialog,
      disabled: loading || !hasExportableContent,
      loading,
      hasOptions: true,
    });
    return () => clearXlsxExport?.('analytics');
  }, [clearXlsxExport, hasExportableContent, loading, openDialog, registerXlsxExport]);

  const handleReset = () => {
    setContent({ ...DEFAULT_ANALYTICS_CONTENT });
    setDailyDetailRange('full');
    setDailyDetailCages('all');
    try {
      window.localStorage.removeItem(ANALYTICS_XLSX_PREFS_KEY);
    } catch (_error) {}
  };

  const handleExport = async () => {
    const estimate = dailyDetailEnabled
      ? estimateDailyDetailRows(data, {
        dailyDetailRange,
        dailyDetailCages,
        selectedDay,
        selectedRateKey,
        selectedEntity,
        compact,
      })
      : 0;
    if (estimate > 200000 && !window.confirm(
      t('export_scope_warning', 'El rango completo generará aproximadamente {{rows}} filas. ¿Continuar?')
        .replace('{{rows}}', estimate.toLocaleString()),
    )) return;

    writeAnalyticsXlsxPreferences(window.localStorage, content, dailyDetailRange);
    setLoading(true);
    setError('');
    try {
      const sheets = buildAnaliticaSheets({
        data,
        nivelSeleccionado,
        groupMode,
        compact,
        content,
        dailyDetailRange,
        dailyDetailCages,
        selectedDay,
        selectedRateKey,
        selectedEntity,
        translate,
        showAlarmReaction: canViewAlarmReaction(localStorage.getItem('cargo')),
        metadata: { region, area, center, dias },
      });
      const blob = await buildXlsx(sheets);
      const entity = nivelSeleccionado === 'region' ? region : nivelSeleccionado === 'area' ? area : center;
      const displayEntity = nivelSeleccionado === 'region' ? labelRegion(region, entity) : nivelSeleccionado === 'area' ? labelArea({ region, area }, entity) : labelCentro({ region, area, centro: center }, entity);
      const date = (value) => String(value || '').replaceAll('/', '-');
      downloadXlsx(
        sanitizeFileName(`Analitica_${displayEntity || nivelSeleccionado}_${date(dias?.startDate)}_${date(dias?.endDate)}.xlsx`),
        blob,
      );
      setOpen(false);
    } catch (exportError) {
      console.error('[SCAPP XLSX] No se pudo exportar Analítica', exportError);
      setError(t('export_error', 'No se pudo generar el archivo Excel.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && setOpen(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { bgcolor: '#0c0c0c', color: '#e6e6e6', border: '1px solid #e84e0e' } }}
    >
      <DialogTitle>{t('export_options', 'Opciones de exportación')} · {t('export_format_excel', 'Excel')}</DialogTitle>
      <DialogContent sx={{ pt: '1rem !important' }}>
        <Typography sx={sectionTitleSx}>{t('export_content_title', 'Contenido del libro')}</Typography>
        <Grid container spacing={1.5}>
          {CONTENT_OPTIONS.map(([key, labelKey, fallback]) => {
            const available = availability[key];
            const label = t(labelKey, fallback);
            const unavailableReason = key === 'dailyDetail' && nivelSeleccionado !== 'centro'
              ? t('export_info_no_daily_detail', 'El detalle diario solo está disponible a nivel centro.')
              : t('export_no_data_in_range', 'Sin datos en el rango');
            return (
              <Grid item xs={6} key={key}>
                <Grid container alignItems="center" wrap="nowrap" columnSpacing={1}>
                  <Grid item>
                    <ScappSwitch
                      ariaLabel={label}
                      isChecked={content[key]}
                      disabled={loading || !available}
                      onToggle={() => setContent((current) => ({ ...current, [key]: !current[key] }))}
                    />
                  </Grid>
                  <Grid item>
                    <Typography sx={{ fontSize: '.78rem' }}>{label}</Typography>
                    {!available && (
                      <Typography sx={{ color: '#9ca3af', fontSize: '.68rem' }}>{unavailableReason}</Typography>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </Grid>

        <Divider sx={{ my: 2, borderColor: 'rgba(232,78,14,.25)' }} />
        <Typography sx={sectionTitleSx}>{t('export_range_title', 'Rango temporal del detalle diario')}</Typography>
        <RadioGroup
          row
          value={dailyDetailRange}
          onChange={(event) => setDailyDetailRange(event.target.value)}
        >
          <FormControlLabel
            value="full"
            disabled={loading || !dailyDetailEnabled}
            control={<Radio size="small" />}
            label={t('export_range_full', 'Todo el rango')}
          />
          <FormControlLabel
            value="day"
            disabled={loading || !dailyDetailEnabled || !selectedDay}
            control={<Radio size="small" />}
            label={t('export_range_day', 'Solo el día seleccionado')}
          />
        </RadioGroup>
        {dailyDetailEnabled && !selectedDay && (
          <Typography sx={{ color: '#9ca3af', fontSize: '.68rem' }}>
            {t('export_range_requires_day', 'Requiere seleccionar un día en el gráfico')}
          </Typography>
        )}

        <Divider sx={{ my: 2, borderColor: 'rgba(232,78,14,.25)' }} />
        <Typography sx={sectionTitleSx}>{t('export_cages_title', 'Jaulas del detalle diario')}</Typography>
        <RadioGroup
          row
          value={dailyDetailCages}
          onChange={(event) => setDailyDetailCages(event.target.value)}
        >
          <FormControlLabel
            value="all"
            disabled={loading || !dailyDetailEnabled}
            control={<Radio size="small" />}
            label={t('export_cages_all', 'Todas las jaulas')}
          />
          <FormControlLabel
            value="selected"
            disabled={loading || !dailyDetailEnabled || !selectedEntity}
            control={<Radio size="small" />}
            label={t('export_cages_selected', 'Solo la jaula seleccionada')}
          />
        </RadioGroup>
        {dailyDetailEnabled && !selectedEntity && (
          <Typography sx={{ color: '#9ca3af', fontSize: '.68rem' }}>
            {t('export_cages_requires_cage', 'Requiere seleccionar una jaula en el gráfico')}
          </Typography>
        )}

        {error && <Typography sx={{ mt: 1.5, color: '#ff9f7e' }}>{error}</Typography>}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid rgba(232,78,14,.35)', p: 2 }}>
        <Button onClick={handleReset} disabled={loading} sx={{ mr: 'auto', color: '#e84e0e' }}>
          {t('export_reset_preferences', 'Restablecer')}
        </Button>
        <Button onClick={() => setOpen(false)} disabled={loading} sx={{ color: '#cbd5e1' }}>
          {t('cancel', 'Cancelar')}
        </Button>
        <Button variant="outlined" onClick={handleExport} disabled={loading} sx={{ color: '#e84e0e', borderColor: '#e84e0e' }}>
          {loading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: '#e84e0e' }} />
              {t('generating_excel', 'Generando Excel...')}
            </>
          ) : t('generate_excel', 'Generar Excel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AnalyticsXlsxExportButton;
